import chunk from 'lodash/chunk';
import dateUtil from '../scripts/utils/date';
import { range } from '../scripts/utils/numbers';

/**
 * 返回指定年、月的第一天日期
 * @param {Object} { year, month }
 * @returns {Date}
 */
function getFirstDayOfMonth({ year, month }) {
  return new Date(year, month, 1);
}

/**
 * 返回指定年、月的天数
 * @param {Object} { year, month }
 * @returns {Number}
 */
function getDaysInMonth({ year, month }) {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * 返回指定年、月的最后一天日期
 * @param {Object} { year, month }
 * @returns {Date}
 */
function getLastDayOfMonth({ year, month }) {
  return new Date(year, month, getDaysInMonth({ year, month }));
}

function isSameYear(date1, date2) {
  return date1.getFullYear() === date2.getFullYear();
}

function isSameMonth(date1, date2) {
  return isSameYear(date1, date2) && date1.getMonth() === date2.getMonth();
}

function isSameDate(date1, date2) {
  return isSameMonth(date1, date2) && date1.getDate() === date2.getDate();
}

function isSameTimeHour(time1, time2) {
  return time1.getHours() === time2.getHours();
}

function isSameTimeMins(time1, time2) {
  return isSameTimeHour(time1, time2) && time1.getMinutes() === time2.getMinutes();
}

function isSameTimeSecs(time1, time2) {
  return isSameTimeMins(time1, time2) && time1.getSeconds() === time2.getSeconds();
}

/**
 * 是否是某法范围内的日期，精确到日
 * @param {Date} value 目标日期
 * @param {Object} { start, end } 范围
 * @returns {Boolean}
 */
function isBetween(value, { start, end }) {
  const date = new Date(value.getFullYear(), value.getMonth(), value.getDate());

  const startTime = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endTime = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  return startTime <= date && endTime >= date;
}

/**
 * 比较两个日期对象的时间戳
 * @param {Date} date1 日期1
 * @param {Date} date2 日期2
 * @returns {Number} 返回 date1.getTime() - date2.getTime() 的差值
 */
function compareAsc(date1, date2) {
  const d1 = date1.getTime();
  const d2 = date2.getTime();

  if (d1 < d2) return -1;
  if (d1 > d2) return 1;
  return 0;
}

/**
 * 比较两个 Date 的时间部分是否是同一秒 或则 同一分 或则 同一小时
 * @param {Date} date1 比较的日期
 * @param {Date} date2 比较的日期
 * @param {String} type 比较类型，默认比较到『秒』 secs|mins|hour
 * @returns {Boolean}
 */
export function isSameTime(time1, time2, type = 'secs') {
  const func = {
    isSameTimeHour,
    isSameTimeMins,
    isSameTimeSecs,
  };
  return func[`isSameTime${firstUpperCase(type)}`](time1, time2);
}

/**
 * 比较两个 Date 是否是同一天 或则 同一月 或则 同一年
 * @param {Date} date1 比较的日期
 * @param {Date} date2 比较的日期
 * @param {String} type 比较类型，默认比较到『日』 date|month|year
 * @returns {Boolean}
 */
export function isSame(date1, date2, type = 'date') {
  const func = {
    isSameYear,
    isSameMonth,
    isSameDate,
  };
  return func[`isSame${firstUpperCase(type)}`](date1, date2);
}

export function outOfRanges(d, min, max) {
  return (min && compareAsc(d, min) === -1) || (max && compareAsc(d, max) === 1);
}

/**
 * 比较两个 Date 的时间部分，忽略年月日
 * @param {Date} time1 日期1
 * @param {Date} time2 日期2
 * @returns {Number} 返回值：相等0，小于 -1，大于 1
 */
export function compareTime(time1, time2) {
  return compareAsc(time1, cloneTime(time2, time1));
}

/**
 * @returns {Date} 当天零点的日期对象
 */
export function getToday() {
  return setDateTime(new Date(), 0, 0, 0);
}

/**
 * 返回日期对象的年、月、日、小时、分钟、秒、12小时制标识
 * @param {Date} date
 * @returns {Object}
 */
export function getDateObj(date) {
  if (!(date instanceof Date)) {
    date = getToday();
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    date: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
    seconds: date.getSeconds(),
    meridiem: date.getHours() > 11 ? 'PM' : 'AM',
  };
}

/**
 * 设置日期对象的时间部分
 * @param {Date} d 日期
 * @param {Number} hour 小时
 * @param {Number} min 分钟
 * @param {Number} sec 秒
 * @returns {Date} 一个新的date
 */
export function setDateTime(d, hour, min, sec) {
  const { year, month, date } = getDateObj(d);
  return new Date(year, month, date, hour, min, sec, 0);
}

/**
 * 增加月份
 * @param {Date} date 起始日期
 * @param {Number} num 月份数
 * @returns {Date}
 */
export function subtractMonth(date, num) {
  const day = date.getDate();
  const newDate = new Date(date);

  // eslint-disable-next-line no-plusplus
  while (num--) {
    newDate.setDate(0);
  }
  newDate.setDate(day);
  return newDate;
}

/**
 * 减月份
 * @param {Date} date 起始日期
 * @param {Number} num 月份数
 * @returns {Date}
 */
export function addMonth(date, num) {
  if (num < 0) num = 0;
  const newDate = new Date(date);
  newDate.setMonth(date.getMonth() + num);
  return newDate;
}

/**
 * 是否为相邻月份
 * @param {Date} start 开始日期
 * @param {Date} end 结束日期
 * @returns {Boolean}
 */
export function isSiblingMonth(start, end) {
  if (!end) return true;
  return isSame(addMonth(start, 1), end, 'month');
}

/**
 * 克隆 time1 的时间到 time2 上
 * @param {Date} time1 时间目标
 * @param {Date} time2 日期目标
 * @returns {Date} 返回一个日期部分为 time2，时间部分为 time1 的新日期对象
 */
export function cloneTime(time1, time2) {
  const { hours, minutes, seconds } = getDateObj(time1);
  return setDateTime(time2, hours, minutes, seconds);
}

export function getWeeks(
  { year, month },
  {
    firstDayOfWeek,
    disabledDate = () => false,
    minDate,
    maxDate,
  } = {}
) {
  const prependDay = getFirstDayOfMonth({ year, month });

  const appendDay = getLastDayOfMonth({ year, month });

  const maxDays = getDaysInMonth({ year, month });

  const daysArr = [];
  let i = 1;

  const today = getToday();

  for (i; i <= maxDays; i++) {
    const currentDay = new Date(year, month, i);

    daysArr.push({
      text: i,
      active: false,
      value: currentDay,
      disabled: disabledDate(currentDay) || outOfRanges(currentDay, minDate, maxDate),
      now: isSame(today, currentDay),
      firstDayOfMonth: i === 1,
      lastDayOfMonth: i === maxDays,
      type: 'current-month',
    });
  }

  if (prependDay.getDay() !== firstDayOfWeek) {
    prependDay.setDate(0); // 上一月

    while (true) { // eslint-disable-line
      daysArr.unshift({
        text: prependDay.getDate().toString(),
        active: false,
        value: new Date(prependDay),
        disabled: disabledDate(prependDay) || outOfRanges(prependDay, minDate, maxDate),
        additional: true, // 非当前月
        type: 'prev-month',
      });
      prependDay.setDate(prependDay.getDate() - 1);
      if (prependDay.getDay() === Math.abs(firstDayOfWeek + 6) % 7) break;
    }
  }

  const LEN = 42; // 显示6周

  while (daysArr.length < LEN) {
    appendDay.setDate(appendDay.getDate() + 1);
    daysArr.push({
      text: appendDay.getDate(),
      active: false,
      value: new Date(appendDay),
      disabled: disabledDate(appendDay) || outOfRanges(appendDay, minDate, maxDate),
      additional: true, // 非当前月
      type: 'next-month',
    });
  }

  return chunk(daysArr, 7);
}

export function getYears(
  year,
  {
    disabledDate = () => false,
    minDate,
    maxDate,
  } = {}
) {
  const startYear = parseInt(year / 10, 10) * 10;
  const endYear = startYear + 9;

  const yearArr = [];

  const today = getToday();

  for (let i = startYear; i <= endYear; i++) {
    const date = new Date(i, 1);

    let disabledMonth = 0;
    let outOfRangeMonth = 0;

    for (let j = 0; j < 12; j++) {
      const d = new Date(i, j);
      if (disabledDate(d)) disabledMonth += 1;
      if (outOfRanges(d, minDate, maxDate)) outOfRangeMonth += 1;
    }

    yearArr.push({
      value: date,
      now: isSame(date, today, 'year'),
      disabled: disabledMonth === 12 || outOfRangeMonth === 12,
      active: false,
      text: `${date.getFullYear()}`,
    });
  }

  return chunk(yearArr, 3);
}

export function getMonths(year, { disabledDate = () => false, minDate, maxDate } = {}) {
  const MonthArr = [];

  const today = getToday();

  for (let i = 0; i <= 11; i++) {
    const date = new Date(year, i);

    let disabledDay = 0;
    let outOfRangeDay = 0;

    const daysInMonth = getDaysInMonth({ year, month: i });

    for (let j = 1; j <= daysInMonth; j++) {
      const d = new Date(year, i, j);
      if (disabledDate(d)) disabledDay += 1;
      if (outOfRanges(d, minDate, maxDate)) outOfRangeDay += 1;
    }

    MonthArr.push({
      value: date,
      now: isSame(date, today, 'month'),
      disabled: disabledDay === daysInMonth || outOfRangeDay === daysInMonth,
      active: false,
      text: `${date.getMonth() + 1} 月`,
    });
  }

  return chunk(MonthArr, 3);
}

export function flagActive(data, { ...args }) {
  const { start, end, type = 'date' } = args;

  if (!end) {
    return data.map(row => row.map((item) => {
      item.active = isSame(item.value, start, type);
      return item;
    }));
  }

  return data.map(row => row.map((item) => {
    const date = item.value;
    const isStart = isSame(start, date, type);
    const isEnd = isSame(end, date, type);
    item.active = isStart || isEnd;
    item.highlight = isBetween(date, { start, end });
    item.startOfRange = isStart;
    item.endOfRange = isEnd;
    return item;
  }));
}

/**
 * 字符串转 Date 类型
 * @param {String} dirtyDate Date string
 * @param {String} fmt Date parse format
 * @returns {Date|undefined}
 */
export function parseToDate(dateStr, fmt) {
  if (!dateStr) return;
  return dateUtil.parse(dateStr, fmt);
}

/**
 * Date 转字符串
 * @param {Date|null} date Date obj
 * @param {String} fmt Date format string
 * @returns {String}
 */
export function dateToFormat(date, fmt) {
  if (!date) return '';
  return dateUtil.format(date, fmt);
}

/**
 * 解析 format 字符串中 小时、分钟、秒、12小时制标识的类型和位置
 * @param {String} format format字符串
 * @returns {Object} { hourFormat, minFormat, secFormat, meridiemFormat }
 */
export function getFormatInfo(format) {
  const hourFormat = format.match(/HH|H|hh|h/) || [];
  const minFormat = format.match(/mm|m/) || [];
  const secFormat = format.match(/ss|s/) || [];
  const meridiemFormat = format.match(/a|A/) || [];

  return { hourFormat, minFormat, secFormat, meridiemFormat };
}

/**
 * 12小时转24小时
 * @param {Number} hour 1~12
 * @param {String} meridiem AM|PM
 * @returns {Number} 0~24
 */
export function hour12To24(hour, meridiem) {
  meridiem = meridiem.toUpperCase();
  if (meridiem === 'AM') {
    if (hour === 12) return 0;
    return hour;
  }

  if (hour === 12) return 12;
  return hour + 12;
}

/**
 * 24小时转12小时
 * @param {Number} hour 0~24
 * @returns {Number} 1~12
 */
export function hour24To12(hour) {
  if (hour === 0) return 12;
  if (hour >= 1 && hour <= 12) return hour;
  return hour - 12;
}

export function getHours({ hourType, meridiem }, options, selectedValue) {
  const {
    disabledHour = () => false,
    minH } = options;

  if (hourType === 'h' || hourType === 'hh') {
    return range(11, 0).map((i) => {
      const text = i === 0 ? 12 : i;
      const hour24 = hour12To24(text, meridiem);
      return {
        value: text,
        text: padZero(text),
        disabled: disabledHour(hour24) || minH > hour24,
        selected: hour24To12(selectedValue) === text,
      };
    });
  }

  // 默认24小时制
  return range(23, 0).map(i => ({
    value: i,
    text: padZero(i),
    disabled: disabledHour(i) || minH > i,
    selected: selectedValue === i,
  }));
}

export function getMintues(hour, options, selectedValue) {
  const {
    disabledMin = () => false,
    minH,
    minM } = options;

  return range(59, 0).map(i => ({
    value: i,
    text: padZero(i),
    disabled: disabledMin(hour, i) || (hour === minH && minM > i),
    selected: selectedValue === i,
  }));
}

export function getSeconds({ hours, minutes }, options, selectedValue) {
  const {
    disabledSec = () => false,
    minH,
    minM,
    minS } = options;

  return range(59, 0).map(i => ({
    value: i,
    text: padZero(i),
    disabled: disabledSec(hours, minutes, i) || (hours === minH && minutes === minM && minS > i),
    selected: selectedValue === i,
  }));
}

export function getMeridiem(meridiemType, options, selectedValue) {
  const {
    disabledHour = () => false } = options;

  let amDisabled = true;
  let pmDisabled = true;

  for (let i = 0; i < 12; i++) {
    if (!disabledHour(i)) amDisabled = false;
  }

  for (let i = 12; i < 24; i++) {
    if (!disabledHour(i)) pmDisabled = false;
  }

  if (meridiemType === 'a') {
    return ['am', 'pm'].map(i => ({
      value: i.toUpperCase(),
      text: i,
      selected: i === selectedValue.toLowerCase(),
      disabled: i === 'am' ? amDisabled : pmDisabled,
    }));
  }

  return ['AM', 'PM'].map(i => ({
    value: i,
    text: i,
    selected: i === selectedValue,
    disabled: i === 'AM' ? amDisabled : pmDisabled,
  }));
}

/**
 * 小于 10 的数补零
 * @param {Number} num 数
 * @returns {String}
 */
export function padZero(num) {
  return num >= 10 ? num.toString() : `0${num}`;
}

/**
 * 开始一个动画
 * @param {Number} A 起始值
 * @param {Number} B 目标是
 * @param {Number} rate 变化速率
 * @param {Function} callback 回调 (curValue, isFinished) 参数为当前移动的位置，是否结束动画
 */
export function easeout(A, B, rate, callback) {
  if (A === B || typeof A !== 'number') {
    return;
  }
  B = B || 0;
  rate = rate || 2;

  const step = function() {
    A += (B - A) / rate;

    if (Math.abs(A - B) < 1) {
      callback(B, true);
      return;
    }
    callback(A, false);
    requestAnimationFrame(step);
  };
  step();
}

/**
 * 首字母大写
 * @param {String} str 目标字符串
 * @returns {String}
 */
export function firstUpperCase(str) {
  if (!str) return str;
  return str[0].toUpperCase().concat(str.substring(1, str.length));
}
